import type { CellContext } from '@tanstack/react-table';
import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { FormErrorsWithArray, WrappedFieldArrayProps } from 'redux-form';

import * as FileUtils from '@acceligentllc/shared/utils/file';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import type { FileType } from '@acceligentllc/shared/enums/fileType';
import type { ResourceStatus } from '@acceligentllc/shared/enums/resourceStatus';
import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import { loadingToast, successToast } from '@acceligentllc/storybook';

import type { SimpleTableRow } from 'af-fields/SimpleTable';
import SimpleTable from 'af-fields/SimpleTable';

import ImagePreviewModal from 'af-components/ImagePreviewModal';

import * as AttachmentActions from 'af-actions/attachment';

import DocumentPreviewModal from './DocumentPreviewModal';
import type { InvoiceFM } from '../EditWhenInvoiced/formModel';
import styles from '../EditWhenInvoiced/styles.module.scss';

type AttachmentFM = Required<InvoiceFM>['attachments'][0];

interface AttachmentPreview {
	id: number;
	size: number;
	name: string;
	type: FileType;
	status: ResourceStatus;
	storageName: string;
	storageContainer: string;
	/** original version with presigned url */
	originalSrc: string;
	copyToWorkOrderFlag?: boolean;
	/** ISO_DATETIME */
	lastModifiedAt: string;
	uploadedBy: Nullable<{ fullName: string; }>;
	directoryId?: number;
}

export interface OwnProps {
	errors: FormErrorsWithArray<InvoiceFM, string>;
	initialized: boolean;
	onDelete: (attachmentId: number) => void;
}

type Props = OwnProps & WrappedFieldArrayProps<AttachmentFM> & ConnectedProps<typeof connector>;

const AttachmentItems: React.FC<Props> = (props) => {
	const {
		fields,
		errors,
		initialized,
		downloadAttachment,
		downloadAttachmentForPreview,
		onDelete,
	} = props;

	const [modalPreviewDocVisible, setModalPreviewDocVisible] = React.useState(false);
	const [previewAttachment, setPreviewAttachment] = React.useState<Nullable<AttachmentPreview>>(null);
	const [previewData, setPreviewData] = React.useState<ArrayBuffer>();

	const onPreviewDocumentClick = React.useCallback(async (attachment: AttachmentFM & SimpleTableRow) => {
		if (modalPreviewDocVisible) {
			setModalPreviewDocVisible(false);
			return;
		}
		const data = await downloadAttachmentForPreview(attachment.id);
		setPreviewData(data.data);
		setModalPreviewDocVisible(true);
	}, [downloadAttachmentForPreview, modalPreviewDocVisible]);

	const closePreview = React.useCallback(() => {
		setPreviewAttachment(null);
	}, []);

	const openAttachment = React.useCallback((attachment: AttachmentFM & SimpleTableRow) => {
		setPreviewAttachment(attachment);
	}, []);

	const renderPreview = React.useCallback((attachment: AttachmentFM & SimpleTableRow) => {

		if (FileUtils.isImageFile(attachment.storageName)) {
			return (
				<span className={styles['installments__invoiced__list__item__preview__download-preview']}>
					<span className="icon-search pointer" onClick={openAttachment.bind(this, attachment)} />
				</span>
			);
		}

		if (FileUtils.isPDFFile(attachment.storageName)) {
			return (
				<span className={styles['installments__invoiced__list__item__preview__download-preview']}>
					<span className="icon-search pointer" onClick={onPreviewDocumentClick.bind(this, attachment)} />
				</span>
			);
		}

		return (<></>);

	}, [onPreviewDocumentClick, openAttachment]);

	const renderDownload = React.useCallback((attachment: AttachmentFM & SimpleTableRow) => {
		const onDownloadAttachmentsClick = async (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
			e.stopPropagation();

			const startingTimestamp = TimeUtils.formatDate(new Date(), TimeFormat.FULL_DATE_LONG);
			loadingToast(`${attachment.id}`, { title: `Started Downloading ${attachment.fileName}`, timestamp: startingTimestamp });

			await downloadAttachment(attachment.id, attachment.name);

			successToast(`${attachment.id}`, { title: `${attachment.fileName} downloaded`, timestamp: startingTimestamp });
		};
		return (
			<span className={styles['installments__invoiced__list__item__preview__download-preview']} onClick={onDownloadAttachmentsClick.bind(this)}>
				<span className="icon-download pointer" />
			</span>
		);
	}, [downloadAttachment]);

	const onDeleteAttachmentField = React.useCallback((index: number) => {
		const attachment = fields.get(index);
		onDelete(attachment.id);
	}, [fields, onDelete]);

	const columns = React.useMemo(() => [{
		id: 'fileName',
		cell: (_cell: CellContext<AttachmentFM & SimpleTableRow, string>) => {
			return _cell.getValue() ?? 'N/A';
		},
		header: 'File Name',
		accessor: 'fileName',
		enableSorting: true,
	},
	{
		id: 'preview',
		cell: (_cell: CellContext<AttachmentFM & SimpleTableRow, string>) => {
			return renderPreview(_cell.row.original);
		},
		header: 'Preview',
		accessor: 'preview',
	},
	{
		id: 'size',
		cell: (_cell: CellContext<AttachmentFM & SimpleTableRow, number>) => {
			return _cell.getValue() ? _cell.getValue() / 1000 + ' MB' : 'N/A';
		},
		header: 'Size',
		accessor: 'size',
		enableSorting: true,
	},
	{
		id: 'download',
		cell: (_cell: CellContext<AttachmentFM & SimpleTableRow, string>) => {
			return renderDownload(_cell.row.original);
		},
		header: 'Download',
		accessor: 'download',
	},
	{
		id: 'uploadedOn',
		cell: (_cell: CellContext<AttachmentFM & SimpleTableRow, string>) => {
			return TimeUtils.toDatabaseDateOnly(_cell.getValue()) ?? 'N/A';
		},
		header: 'Uploaded On',
		accessor: 'lastModifiedAt',
		enableSorting: true,
	},
	{
		id: 'uploadedBy',
		cell: (_cell: CellContext<AttachmentFM & SimpleTableRow, string>) => {
			const user = _cell.row.original.uploadedBy;
			return !user ? 'N/A' : user.fullName;
		},
		header: 'Uploaded By',
		accessor: 'uploadedBy',
		enableSorting: true,
	},
	], [renderDownload, renderPreview]);

	return (
		<>
			<SimpleTable
				allowDeleteOnly={true}
				allowEdit={true}
				columns={columns}
				errors={errors.attachments}
				fields={fields}
				initialized={initialized}
				onDelete={onDeleteAttachmentField}
			/>
			{previewAttachment && (
				<ImagePreviewModal
					closeModal={closePreview}
					imageSrc={previewAttachment.originalSrc}
					showModal={!!previewAttachment}
				/>
			)}
			{previewData &&
				<DocumentPreviewModal
					data={previewData}
					setModalVisible={setModalPreviewDocVisible}
					showModal={modalPreviewDocVisible}
				/>
			}
		</>
	);
};

function mapDispatchToProps() {
	return {
		downloadAttachment: AttachmentActions.downloadAttachment,
		downloadAttachmentForPreview: AttachmentActions.downloadAttachmentForPreview,
	};
}

const connector = connect(null, mapDispatchToProps());

export default connector(AttachmentItems);
